import { useCallback, useContext } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { odata } from '@bingads-webui/http-util';
import { addAsset, deleteAsset, getImageById } from './image-data';
import { useOdataOptions } from './use-odata-options';
import type { AigcRequestActivity } from './use-generate-business-snapshot';

/**
 * Image file extensions supported by the Asset Library AddAsset endpoint.
 * Mirrors the constant from ads-studio-app-shared-components/src/model/constants.js.
 */
export const SUPPORTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif'];

export interface UploadedImageInfo {
  assetId: string;
  fileName: string;
  fileUrl: string;
  /** Pixel width of the uploaded asset, read back from GET Images. */
  width?: number;
  /** Pixel height of the uploaded asset, read back from GET Images. */
  height?: number;
}

/**
 * Reads a File as a base64-encoded data URL string.
 *
 * @param {File} file - The file to read
 * @returns {Promise<string>} base64 data (without the data URL prefix)
 */
const readFileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = reader.result as string;
    // Strip the "data:image/png;base64," prefix
    const base64 = dataUrl.split(',')[1];
    resolve(base64);
  };
  reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
  reader.readAsDataURL(file);
});

/**
 * Fetches an image from a remote URL and returns its base64-encoded body.
 * Used for AI-discovered images from the business snapshot that need to be
 * re-hosted via AddAsset.
 *
 * @param {string} url - Remote image URL to fetch.
 * @returns {Promise<string>} base64 data (without the data URL prefix)
 */
const fetchAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (${response.status}): ${url}`);
  }
  const blob = await response.blob();
  // Reuse readFileAsBase64 by wrapping the blob into a File-like with a name.
  // We pass an empty name because the file name isn't used by readAsDataURL.
  const file = new File([blob], 'image', { type: blob.type });
  return readFileAsBase64(file);
};

/**
 * Extracts the filename portion of a URL path (after the last /, before query).
 * Falls back to a safe default when no recognizable name is present.
 *
 * @param {string} url - URL to extract a display name from.
 * @returns {string} A display-friendly filename.
 */
const fileNameFromUrl = (url: string): string => {
  try {
    const { pathname } = new URL(url);
    const last = pathname.split('/').filter(Boolean).pop();
    return last || 'image';
  } catch {
    return 'image';
  }
};

/**
 * Calls AddAsset with a base64 image, then fetches the resulting Image
 * entity to capture the CDN-hosted Url. AddAsset's response only carries
 * the new asset id; consumers (Brand Kit write, GenerateBusinessSnapshot)
 * need the Url, so we always do the GET round-trip.
 *
 * @param {string} base64 - Base64-encoded image data.
 * @param {string} fileName - Display name used as the Asset.Text value.
 * @param {string|number} cid - Customer id for the EDM scope.
 * @param {string|number} aid - Account id for the EDM scope.
 * @param {object} odataOptions - host/path/oDataToken passed to the GET.
 * @param {string} sourceType - 'UserUploaded' for direct file uploads or
 *   'WebsiteImported' for AI-discovered images re-hosted from a remote URL.
 *   Persisted on the Asset entity so downstream attribution (campaign
 *   recommendation, AI generation) can distinguish user-supplied assets
 *   from auto-imported ones.
 * @returns {Promise<UploadedImageInfo>} Asset info with id/fileName/fileUrl.
 */
const addImageAsset = async (
  base64: string,
  fileName: string,
  cid: string | number,
  aid: string | number,
  odataOptions: Record<string, unknown>,
  sourceType: 'UserUploaded' | 'WebsiteImported'
): Promise<UploadedImageInfo> => {
  const payload = {
    FolderId: null,
    Asset: {
      '@odata.type': '#Model.Image',
      Text: fileName,
      SourceType: sourceType,
      Data: base64,
      Type: 'GenericImage',
    },
    SupportUpscale: true,
  };

  const response = await addAsset(payload, cid, aid);
  const assetId = response && (response.value || response.Id);

  if (!assetId) {
    return { assetId: '', fileName, fileUrl: '' };
  }

  // Fetch the Image entity to get the CDN-hosted Url + dimensions.
  // Failures don't block the upload — return assetId with empty fileUrl
  // and undefined dimensions rather than tearing the upload down.
  // Snapshot callers that need Url or dimensions will simply skip this image.
  let fileUrl = '';
  let width: number | undefined;
  let height: number | undefined;
  try {
    const image = await getImageById(cid, aid, assetId, odataOptions) as {
      Url?: string;
      Width?: number;
      Height?: number;
      value?: { Url?: string; Width?: number; Height?: number };
    };
    fileUrl = image?.Url ?? image?.value?.Url ?? '';
    width = image?.Width ?? image?.value?.Width;
    height = image?.Height ?? image?.value?.Height;
  } catch {
    fileUrl = '';
  }

  return {
    assetId: String(assetId),
    fileName,
    fileUrl,
    width,
    height,
  };
};

/**
 * Hook that provides a function to upload images via the AddAsset OData endpoint.
 *
 * Uses the existing `Folders/Default.AddAsset` pattern — reads the file as base64,
 * then calls AddAsset with the image data inline.
 *
 * Usage:
 *   const uploadImage = useImageUpload();
 *   const result = await uploadImage(file);
 *   // result = { assetId, fileName, fileUrl }
 *
 * @returns {Function} uploadImage - accepts a File, returns a Promise
 */
export const useAssetLibraryImageUpload = (): ((file: File) => Promise<UploadedImageInfo>) => {
  const serverContext = useContext(ServerContext) as Record<string, any>;
  const currentCustomer = serverContext.CurrentCustomer;
  const currentAccount = serverContext.CurrentAccount;
  const cid = currentCustomer ? currentCustomer.Id : '';
  const aid = currentAccount ? currentAccount.Id : '';
  const [odataOptions] = useOdataOptions();

  return useCallback(async (file: File): Promise<UploadedImageInfo> => {
    const base64 = await readFileAsBase64(file);
    const info = await addImageAsset(base64, file.name, cid, aid, odataOptions, 'UserUploaded');
    return { ...info, fileName: file.name };
  }, [cid, aid, odataOptions]);
};

/**
 * Hook that uploads an image from a remote URL to the Asset Library.
 *
 * Fetches the bytes via `fetch`, base64-encodes them, then forwards to the
 * same AddAsset endpoint used by `useAssetLibraryImageUpload`. Primary use
 * case: re-hosting AI-discovered images (logos, hero images) returned by the
 * business snapshot so they can be referenced by Brand Kit. Tags the asset
 * with `SourceType: 'WebsiteImported'` so downstream consumers can tell
 * these apart from assets the user uploaded directly.
 *
 * @returns {Function} uploadImageByUrl - accepts a URL, returns a Promise
 */
export const useAssetLibraryImageUploadByUrl = (): ((url: string) => Promise<UploadedImageInfo>) => {
  const serverContext = useContext(ServerContext) as Record<string, any>;
  const currentCustomer = serverContext.CurrentCustomer;
  const currentAccount = serverContext.CurrentAccount;
  const cid = currentCustomer ? currentCustomer.Id : '';
  const aid = currentAccount ? currentAccount.Id : '';
  const [odataOptions] = useOdataOptions();

  return useCallback(async (url: string): Promise<UploadedImageInfo> => {
    const base64 = await fetchAsBase64(url);
    const fileName = fileNameFromUrl(url);
    return addImageAsset(base64, fileName, cid, aid, odataOptions, 'WebsiteImported');
  }, [cid, aid, odataOptions]);
};

/**
 * Hook that returns a function for deleting an image from the Asset Library.
 *
 * Calls `Folders/Default.DeleteAsset` with the numeric asset id. Returns a
 * Promise that resolves on success and rejects on backend failure — callers
 * can ignore the rejection and still drop the image from local state if
 * "best-effort cleanup" semantics are acceptable.
 *
 * @returns {Function} deleteImage - accepts an asset id, returns a Promise
 */
// eslint-disable-next-line no-unused-vars
export const useAssetLibraryImageDelete = (): ((assetId: string | number) => Promise<unknown>) => {
  const serverContext = useContext(ServerContext) as Record<string, any>;
  const currentCustomer = serverContext.CurrentCustomer;
  const currentAccount = serverContext.CurrentAccount;
  const cid = currentCustomer ? currentCustomer.Id : '';
  const aid = currentAccount ? currentAccount.Id : '';

  return useCallback((assetId: string | number) => deleteAsset(cid, aid, assetId), [cid, aid]);
};

/**
 * Hook that returns a function to fetch a single Asset Library image's
 * `Url`, `Width`, `Height` by id. Used after `useAssetLibraryImageBulkUploadByUrl`
 * to resolve dimensions per asset (the bulk response only carries Id+Url,
 * not pixel dimensions). Safe to call in parallel via `Promise.all`.
 *
 * @returns {Function} getImageDetails - accepts an assetId, returns a Promise
 */
export const useAssetLibraryGetImageById = (): (
  // eslint-disable-next-line no-unused-vars
  (assetId: string | number) => Promise<UploadedImageInfo>
) => {
  const serverContext = useContext(ServerContext) as Record<string, any>;
  const currentCustomer = serverContext.CurrentCustomer;
  const currentAccount = serverContext.CurrentAccount;
  const cid = currentCustomer ? currentCustomer.Id : '';
  const aid = currentAccount ? currentAccount.Id : '';
  const [odataOptions] = useOdataOptions();

  return useCallback(async (assetId: string | number): Promise<UploadedImageInfo> => {
    try {
      const image = await getImageById(cid, aid, assetId, odataOptions) as {
        Url?: string;
        Width?: number;
        Height?: number;
        Text?: string;
        value?: { Url?: string; Width?: number; Height?: number; Text?: string };
      };
      const fileUrl = image?.Url ?? image?.value?.Url ?? '';
      const width = image?.Width ?? image?.value?.Width;
      const height = image?.Height ?? image?.value?.Height;
      const fileName = image?.Text ?? image?.value?.Text ?? '';
      return {
        assetId: String(assetId),
        fileName,
        fileUrl,
        width,
        height,
      };
    } catch {
      return { assetId: String(assetId), fileName: '', fileUrl: '' };
    }
  }, [cid, aid, odataOptions]);
};

// --- Bulk upload by URL (server-side fetch) ------------------------------

/**
 * Single entry in the result of a bulk URL upload. `assetId` is present
 * on success; `error` carries the MT-side error code when the URL
 * couldn't be ingested (URL unreachable, format invalid, etc.). The
 * `originalUrl` is always present so callers can correlate results back
 * to the input list.
 *
 * Note: the bulk endpoint echoes the input `Url` (with `%2C` comma
 * encoding) rather than the rehosted asset URL, so we deliberately do
 * NOT expose a `fileUrl` field here — callers that need the rehosted
 * URL should follow up with `useAssetLibraryGetImageById(assetId)`.
 */
export interface BulkUploadByUrlResult {
  originalUrl: string;
  assetId?: string;
  error?: string;
}

const BULK_UPLOAD_API_NAME = 'odataapi/campaign/v2/customers/accounts/default.bulkupsert';

/**
 * Serializes URLs into the CSV body accepted by `Default.BulkUpsert (Account-scoped)`.
 * Mirrors the format used by the legacy image-experience package
 * (`private/components/packages/image-odata/src/bulkify.js`):
 *
 *   row 0: 'Type,Status,Sub Type,Id, Name,Parent Id,Url,Base id,Metadata,Source Type'
 *   row 1: 'Format Version,,,,6,,,,'
 *   rows 2+: per-URL data rows
 *
 * Each data row: `Image,Active,GenericImage,,,<aid>,<url>,,,WebsiteImported`.
 * Commas inside URLs are percent-encoded so they don't split the row.
 * CR/LF in URLs are stripped — bare newlines would otherwise inject new
 * CSV rows and let an attacker-controlled URL forge asset entries.
 *
 * @param {string[]} urls - URLs to ingest.
 * @param {string|number} aid - Account id used as the parent.
 * @returns {string[]} CSV rows ready for the `CsvRows` POST body.
 */
const serializeBulkUploadByUrlCsv = (urls: string[], aid: string | number): string[] => {
  if (urls.length === 0) return [];
  const header = 'Type,Status,Sub Type,Id, Name,Parent Id,Url,Base id,Metadata,Source Type';
  const fmt = 'Format Version,,,,6,,,,';
  const dataRows = urls.map((url) => {
    // Strip CR/LF first so they can't inject extra CSV rows, then
    // percent-encode commas so they don't split the current row.
    const safe = url.replace(/[\r\n]+/g, '').replace(/,/g, encodeURIComponent(','));
    return `Image,Active,GenericImage,,,${aid},${safe},,,WebsiteImported`;
  });
  return [header, fmt, ...dataRows];
};

/**
 * Parses the CSV-shaped response from `Default.BulkUpsert (Account-scoped)` into one
 * entry per input row. The response is a 2-D array:
 *
 *   [
 *     ['Type', 'Status', 'Id', 'Parent Id', 'Sub Type', 'Name', 'Url', 'Error', ...],
 *     ['', ''] // format version row
 *     [data row 1],
 *     [data row 2],
 *     ...
 *   ]
 *
 * Correlation strategy:
 *   1. When the response carries exactly one data row per input URL, map
 *      back by INDEX. This is the contract MT preserves (verified against
 *      the legacy image-experience bulk path) and is robust to URL
 *      normalization on the wire (e.g. server may echo `%2C` for
 *      literal commas the serializer escaped) and to duplicate input URLs
 *      (which would otherwise collide in a URL-keyed Map).
 *   2. When row count differs, fall back to a URL-keyed Map with
 *      decode-on-lookup so percent-encoded commas still match. Duplicate
 *      input URLs in this fallback get sequential bucket assignment
 *      (`shift()`) so each input slot gets a distinct row.
 *   3. Any input slot left unmatched returns `{ originalUrl }` with no
 *      assetId and no error so the caller can distinguish correlation
 *      misses from explicit MT errors.
 *
 * Why not URL-only: the legacy CSV serializer percent-encodes commas
 * inside URLs to keep CSV row-splitting unambiguous; some downstream
 * paths echo the encoded form, others decode. Index-order skips that
 * ambiguity entirely on the happy path.
 *
 * @param {unknown} responseJson - Raw CSV array returned by odata.post.
 * @param {string[]} inputUrls - The URLs we sent, in input order.
 * @returns {BulkUploadByUrlResult[]} One entry per input URL, in input order.
 */
const parseBulkUploadByUrlResponse = (
  responseJson: unknown,
  inputUrls: string[]
): BulkUploadByUrlResult[] => {
  if (!Array.isArray(responseJson) || responseJson.length < 3) {
    return inputUrls.map(originalUrl => ({ originalUrl }));
  }
  const rows = responseJson as string[][];
  const header = rows[0];
  if (!Array.isArray(header)) {
    return inputUrls.map(originalUrl => ({ originalUrl }));
  }
  const idIndex = header.indexOf('Id');
  const urlIndex = header.indexOf('Url');
  const errorIndex = header.indexOf('Error');

  // Skip the header (row 0) and the format-version row (row 1) per the
  // bulk-upsert CSV contract; the rest are data rows.
  const dataRows = rows.slice(2).filter(Array.isArray);

  // Happy path: 1-to-1 row-per-input correlation by index. MT preserves
  // submission order in the legacy bulk path, so when counts match we
  // trust the index and skip URL matching entirely.
  if (dataRows.length === inputUrls.length) {
    return inputUrls.map((originalUrl, i) => {
      const row = dataRows[i];
      const id = idIndex >= 0 ? row[idIndex] : undefined;
      const error = errorIndex >= 0 ? row[errorIndex] : undefined;
      return {
        originalUrl,
        assetId: id || undefined,
        error: error || undefined,
      };
    });
  }

  // Fallback: partial / out-of-order response — bucket data rows by URL
  // (decoded on the response side so `%2C`-encoded commas match the
  // unencoded input). Use Map<url, Row[]> so duplicate input URLs each
  // consume a distinct row via shift().
  const decodeUrl = (s: string | undefined): string | undefined => {
    if (!s) return s;
    try {
      // Reverse the comma-encoding the serializer applies; leave the rest
      // of the URL untouched (full decodeURIComponent would over-decode
      // legitimate %xx escapes in the original).
      return s.replace(/%2C/gi, ',');
    } catch {
      return s;
    }
  };

  const byUrl = new Map<string, Array<{ assetId?: string; error?: string }>>();
  dataRows.forEach((row) => {
    const url = urlIndex >= 0 ? decodeUrl(row[urlIndex]) : undefined;
    const id = idIndex >= 0 ? row[idIndex] : undefined;
    const error = errorIndex >= 0 ? row[errorIndex] : undefined;
    if (url) {
      const list = byUrl.get(url) ?? [];
      list.push({ assetId: id || undefined, error: error || undefined });
      byUrl.set(url, list);
    }
  });

  return inputUrls.map((originalUrl) => {
    const bucket = byUrl.get(originalUrl);
    const match = bucket?.shift();
    if (!match) {
      return { originalUrl };
    }
    return {
      originalUrl,
      assetId: match.assetId,
      error: match.error,
    };
  });
};

/**
 * Hook that bulk-uploads multiple image URLs to the Asset Library in a
 * single round-trip via `Default.BulkUpsert` (the top-level Account-
 * scoped action, NOT entity-set-scoped — calling it on `/Images/...`
 * 404s). MT fetches each URL
 * **server-side**, which is the only way to ingest URLs from third-party
 * CDNs that don't return CORS headers (the common shape for AI-discovered
 * snapshot images).
 *
 * This complements `useAssetLibraryImageUploadByUrl`, which fetches via the
 * BROWSER and therefore CORS-fails for most cross-origin AI URLs.
 *
 * Returns one result entry per input URL, in input order. Successful
 * entries carry `assetId`; failed entries carry `error`. After the bulk
 * call, callers typically follow up with `getImageById` to read `Url`,
 * `Width`, `Height` per successful asset.
 *
 * @returns {Function} bulkUploadImagesByUrl - accepts URL[] and an optional
 *   activity logger, returns Promise<BulkUploadByUrlResult[]>
 */
export const useAssetLibraryImageBulkUploadByUrl = (): (
  // eslint-disable-next-line no-unused-vars
  (urls: string[], activity?: AigcRequestActivity) => Promise<BulkUploadByUrlResult[]>
) => {
  const serverContext = useContext(ServerContext) as Record<string, any>;
  const currentCustomer = serverContext.CurrentCustomer;
  const currentAccount = serverContext.CurrentAccount;
  const cid = currentCustomer ? currentCustomer.Id : '';
  const aid = currentAccount ? currentAccount.Id : '';
  const [odataOptions] = useOdataOptions();

  return useCallback(async (
    urls: string[],
    activity?: AigcRequestActivity
  ): Promise<BulkUploadByUrlResult[]> => {
    if (!urls || urls.length === 0) return [];
    const csvRows = serializeBulkUploadByUrlCsv(urls, aid);
    // Default.BulkUpsert is a top-level Account-scoped action (NOT
    // entity-set-scoped). Routing it through any entity set returns 404 —
    // confirmed by image-experience's image-odata.js:266 (legacy CSV bulk
    // path) and use-bulk-upsert-campaign-brand-kit.ts:249 (PMax campaign
    // bulk path), both of which post to the same Account-level action.
    const url = `Customers(${cid})/Accounts(${aid})/Default.BulkUpsert`;
    try {
      const response = await odata.post(url, {
        ...odataOptions,
        data: { CsvRows: csvRows },
      } as any);
      return parseBulkUploadByUrlResponse(response, urls);
    } catch (error) {
      // Whole-batch failure: return error entries for each URL so callers
      // can drop them uniformly. Per-URL failures come back as `error`
      // entries in the success-path response above. The catch itself only
      // sees the rejected jqXHR (often status 0 — a browser-side transport
      // abort, e.g. navigation/tab-suspend — even when MT never received
      // or fully processed the request), so log the raw transport details
      // here; downstream code only re-logs the generic 'BulkUpsertFailed'
      // string per URL and would otherwise discard this diagnosis.
      const xhr = error as {
        status?: unknown;
        readyState?: unknown;
        getResponseHeader?: (name: string) => string | null;
      };
      const status = typeof xhr?.status === 'number' ? xhr.status : undefined;
      const readyState = typeof xhr?.readyState === 'number' ? xhr.readyState : undefined;
      let requestId: string | undefined;
      try {
        requestId = typeof xhr?.getResponseHeader === 'function'
          ? xhr.getResponseHeader('x-ms-requestid') ?? undefined
          : undefined;
      } catch {
        // Keep the fallback behavior when response headers are unavailable.
      }
      activity?.error(
        `[PMaxLiteSignup][AgenticOnboarding] Bulk image rehost transport catch — count=${urls.length} httpStatus=${status ?? 'unknown'} readyState=${readyState ?? 'unknown'}`,
        BULK_UPLOAD_API_NAME,
        requestId
      );
      return urls.map(originalUrl => ({ originalUrl, error: 'BulkUpsertFailed' }));
    }
  }, [cid, aid, odataOptions]);
};

// --- Test-only exports ---------------------------------------------------
// Direct exports of the bulk-upload CSV serializer/parser for unit tests.
// The production callers (useAssetLibraryImageBulkUploadByUrl) already
// use these via closure; the hook itself is harder to test in isolation
// because it depends on ServerContext + odata.post wiring. Tests that
// pin the wire-format contract (column order, %2C handling, partial
// responses, duplicate URLs) live alongside the integration tests in
// pmax-agentic-snapshot-review and import these directly.
export const testOnly = {
  serializeBulkUploadByUrlCsv,
  parseBulkUploadByUrlResponse,
};
